// src/pages/Admin/UserCompanyAdminPage.jsx
import React, { useEffect, useMemo, useState } from 'react';
import {
  Paper, Stack, Typography, TextField, FormControl, InputLabel, Select, MenuItem,
  Button, Chip, Box, IconButton, Tooltip
} from '@mui/material';
import { DataGrid } from '@mui/x-data-grid';
import { PersonAdd, Link as LinkIcon, DeleteOutline } from '@mui/icons-material';
import { RBAC } from '../../services/api';
import { useAuth } from '../../auth/AuthProvider';

export default function UserCompanyAdminPage() {
  const { isSuperuser } = useAuth();
  const [companies, setCompanies] = useState([]);
  const [roles, setRoles] = useState([]);
  const [selCompany, setSelCompany] = useState('');
  const [assignments, setAssignments] = useState([]); // chips visibles
  const [form, setForm] = useState({ user_id_or_email: '', role: '' });
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    (async () => {
      try {
        // empresas accesibles para el actor
        const res = await RBAC.companiesMine();
        const cs = (res.data || [])
          .map(x => x.company)
          .filter(Boolean)
          .map(c => ({ id: c.id, name: c.name }));
        setCompanies(cs);
        if (cs.length) setSelCompany(cs[0].id);
      } catch {}
    })();
  }, []);

  useEffect(() => {
    (async () => {
      if (!selCompany) return;
      try {
        const r = await RBAC.rolesByCompany(selCompany);
        setRoles(r.data || []);
      } catch { setRoles([]); }
    })();
  }, [selCompany]);

  // TODO: reemplaza por tu endpoint real de “asignaciones” si lo tienes.
  // Por ahora mostramos las del actor sólo como UI helper.
  useEffect(() => {
    (async () => {
      try {
        const res = await RBAC.userCompanies('me');
        const list = (res.data || []).map(a => ({
          id: `${a.user?.id}-${a.company?.id}`,
          user: a.user,
          company: a.company,
          role: a.role,
        }));
        setAssignments(list);
      } catch { setAssignments([]); }
    })();
  }, []);

  const addAssignment = async () => {
    if (!(selCompany && form.user_id_or_email && form.role)) return;
    setLoading(true);
    try {
      // backend debe aceptar user_id o email (ajústalo si tu endpoint requiere nombre del campo)
      const payload = {
        company_id: selCompany,
        role: form.role,
        is_active: true,
        // si tu endpoint se llama user_id, envía user_id; si puede email, expón user_identifier, etc.
        user_identifier: form.user_id_or_email,
        extra_permission_ids: []
      };
      await RBAC.assignUserCompany(payload);
      setForm({ user_id_or_email: '', role: '' });
      // refrescamos chips
      const res = await RBAC.userCompanies('me');
      const list = (res.data || []).map(a => ({
        id: `${a.user?.id}-${a.company?.id}`,
        user: a.user,
        company: a.company,
        role: a.role,
      }));
      setAssignments(list);
    } catch (e) {
      // noop
    } finally { setLoading(false); }
  };

  const columns = useMemo(() => ([
    {
      field: 'user',
      headerName: 'Usuario',
      flex: 1,
      minWidth: 220,
      renderCell: ({ row }) => {
        const u = row.user || {};
        const name = [u.first_name, u.last_name].filter(Boolean).join(' ').trim() || u.username || '-';
        return (
          <Stack direction="row" spacing={1} alignItems="center">
            <Box sx={{ width: 26, height: 26, borderRadius: '50%', bgcolor: '#10173c', color: '#fff',
              display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 12, fontWeight: 700 }}>
              {(u.first_name?.[0] || u.username?.[0] || '?').toUpperCase()}
            </Box>
            <Stack>
              <Typography fontWeight={700} fontSize={13}>{name}</Typography>
              <Typography fontSize={12} color="text.secondary">{u.email || '-'}</Typography>
            </Stack>
          </Stack>
        );
      }
    },
    { field: 'company', headerName: 'Empresa', width: 220, renderCell: ({ row }) => row.company?.name || '-' },
    { field: 'role', headerName: 'Rol', width: 180, renderCell: ({ row }) => row.role?.name || '-' },
    {
      field: 'actions',
      headerName: 'Acciones',
      width: 110,
      sortable: false,
      renderCell: () => (
        <Tooltip title="(Wire) remover asignación desde backend real">
          <span>
            <IconButton size="small" disabled><DeleteOutline fontSize="small" /></IconButton>
          </span>
        </Tooltip>
      )
    }
  ]), []);

  return (
    <Paper sx={{ p: 2 }}>
      <Stack direction="row" justifyContent="space-between" alignItems="center" sx={{ mb: 2 }}>
        <Typography variant="h5" sx={{ fontWeight: 800 }}>Usuarios</Typography>
        <Stack direction="row" spacing={2} alignItems="center">
          <FormControl size="small" sx={{ minWidth: 240 }}>
            <InputLabel>Empresa</InputLabel>
            <Select value={selCompany} label="Empresa" onChange={e => setSelCompany(e.target.value)}>
              {companies.map(c => <MenuItem key={c.id} value={c.id}>{c.name}</MenuItem>)}
            </Select>
          </FormControl>
          <TextField
            size="small"
            placeholder="ID o email del usuario…"
            value={form.user_id_or_email}
            onChange={(e) => setForm(v => ({ ...v, user_id_or_email: e.target.value }))}
            sx={{ minWidth: 260 }}
          />
          <FormControl size="small" sx={{ minWidth: 220 }}>
            <InputLabel>Rol</InputLabel>
            <Select value={form.role} label="Rol" onChange={e => setForm(v => ({ ...v, role: e.target.value }))}>
              {roles.map(r => <MenuItem key={r.id} value={r.id}>{r.name}</MenuItem>)}
            </Select>
          </FormControl>
          <Button
            variant="contained"
            startIcon={<PersonAdd />}
            onClick={addAssignment}
            disabled={loading}
          >
            Asignar
          </Button>
        </Stack>
      </Stack>

      {/* Chips resumen (asignaciones del actor) */}
      {!!assignments.length && (
        <Stack direction="row" spacing={1} sx={{ mb: 1, flexWrap: 'wrap' }}>
          {assignments.map(a => (
            <Chip
              key={a.id}
              icon={<LinkIcon />}
              label={`${a.user?.username || a.user?.email || 'user'} → ${a.company?.name || 'empresa'} (${a.role?.name || 'rol'})`}
              variant="outlined"
              sx={{ borderColor: '#10173c' }}
            />
          ))}
        </Stack>
      )}

      <Box sx={{ height: 440 }}>
        <DataGrid
          rows={assignments}
          columns={columns}
          getRowId={(r) => r.id}
          pageSizeOptions={[5, 10, 20]}
          initialState={{ pagination: { paginationModel: { pageSize: 10 } } }}
          sx={{
            '& .MuiDataGrid-columnHeaders': { backgroundColor: '#10173c', color: '#fff' },
            '& .MuiDataGrid-columnHeader, & .MuiDataGrid-columnSeparator': { color: '#fff !important' },
            '& .MuiDataGrid-row:hover': { backgroundColor: 'rgba(16,23,60,0.08)' }
          }}
        />
      </Box>
    </Paper>
  );
}
